/**
 * Контекст страницы для ИИ-агента (ADR-0010).
 *
 * Каждое сообщение агенту несёт СНИМОК того, что открыто у участника:
 * маршрут, уровень, объект из адреса и срез (оси `as_of`/`include_demo` и
 * фильтры вида, параметры ScopeBar). Это умолчание собирается здесь, из
 * адреса, — без участия страниц. Страница, которой есть что добавить
 * (выбранная строка, открытая карточка), дополняет снимок хуком
 * `useAgentContext({...})`; дополнение снимается при уходе со страницы.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import type { LevelId } from '../lib/levels';
import { localIsoDate } from '../lib/localDate';
import { knownServerToday } from '../api/serverToday';

export type PageExtras = Record<string, unknown>;

export interface PageContext {
  route: string;
  /** Шаблон маршрута (`/massifs/:id`) — чтобы модель не разбирала адрес сама. */
  pattern: string | null;
  level: LevelId;
  object: { kind: LevelId; id: string } | null;
  as_of: string | null;
  include_demo: boolean;
  /** Срез вида и параметры ScopeBar из адреса. */
  slice: Record<string, string>;
  title: string | null;
  extras: PageExtras;
  /** Сегодняшняя дата браузера (ГГГГ-ММ-ДД) — модель своих часов не знает. */
  today?: string;
  /** Подписи и значения выбранных фильтров ScopeBar (дата, культура, год, территория, демо). */
  scope_bar?: string | null;
  /** Видимый текст основной области страницы, сжатый до SCREEN_TEXT_CHARS. */
  screen_text?: string | null;
  /** Роль персоны (hokim, manager, observer, farmer): какие пункты меню у участника (6al.23). */
  persona_role?: string | null;
  /** Страница сохранённого отчёта: «правка» берёт отчёт отсюда (agroai-6al.60). */
  saved_report_id?: string | null;
  /** Открытый шаг тура гида: вопрос на шаге — в его контексте (6al.23). */
  tour?: { title: string; step: number; of: number; page: string; step_title: string; regions: string[] } | null;
}

/** Потолок текста экрана в контексте — чтобы снимок не раздувал промпт. */
export const SCREEN_TEXT_CHARS = 6000;

const squeeze = (text: string): string => text.replace(/[ \t\u00a0]+/g, ' ').replace(/\s*\n\s*/g, '\n').trim();

/** Текст основной области без самой панели агента; `null`, если области нет. */
export function readScreenText(limit = SCREEN_TEXT_CHARS): string | null {
  if (typeof document === 'undefined') return null;
  const main = document.querySelector('main') ?? document.getElementById('content');
  if (!main) return null;
  const clone = main.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.agent-panel, .agent-fab, script, style, svg').forEach((el) => el.remove());
  // cloneNode вне документа не даёт innerText — собираем textContent с переводами строк у блоков.
  clone.querySelectorAll('tr, li, p, h1, h2, h3, h4, div, br').forEach((el) => el.append('\n'));
  const text = squeeze(clone.textContent ?? '');
  if (!text) return null;
  // i18n-ignore: уходит модели (снимок текста экрана в запросе к серверу)
  return text.length > limit ? `${text.slice(0, limit)}\n…[текст экрана обрезан]` : text;
}

function readScopeBar(): string | null {
  if (typeof document === 'undefined') return null;
  const bar = document.querySelector('.astra-scope');
  if (bar) return squeeze((bar.textContent ?? '').replace(/(?<=[а-яa-z0-9])(?=[А-ЯA-Z])/g, ' ')) || null;
  // Экраны без ScopeBar (новый «Обзор района») называют свои фильтры сами:
  // `data-agent-scope="Цикл: … · Массив: … · Дата среза: …"`.
  const own = document.querySelector('[data-agent-scope]')?.getAttribute('data-agent-scope');
  return own ? squeeze(own) || null : null;
}

/** «Сегодня» для ИИ — число сервера, когда оно уже известно; иначе часы браузера. */
const localToday = (): string => knownServerToday() ?? localIsoDate();

interface AgentContextValue {
  /** Снимок на момент вызова — читает транспорт перед отправкой. */
  snapshot: () => PageContext;
  setExtras: (owner: string, extras: PageExtras | null) => void;
}

const AgentCtx = createContext<AgentContextValue | null>(null);

/** Шаблоны адресов с объектом и их уровень. Остальное — район. */
const OBJECT_ROUTES: { path: string; level: LevelId }[] = [
  { path: '/massifs/:id', level: 'massif' },
  { path: '/farms/:id', level: 'actor' },
  { path: '/contours/:id', level: 'contour' },
  { path: '/cycles/:id/works/:workId', level: 'work' },
  { path: '/works/:workId', level: 'work' },
  { path: '/cycles/:id', level: 'district' },
];

/** Параметры адреса, которые описывают срез: оси канона, вид и ScopeBar. */
const SLICE_PARAMS = [
  'season',
  'view_massif_id',
  'view_actor_id',
  'cycle',
  'date',
  'massif',
  'farm',
];

export function pageContextFrom(pathname: string, search: string, extras: PageExtras = {}): PageContext {
  const params = new URLSearchParams(search);
  const matched = matchRoutes(OBJECT_ROUTES, pathname)?.[0];
  const spec = matched ? OBJECT_ROUTES.find((r) => r.path === matched.route.path) : undefined;
  const objectId = matched ? (matched.params.workId ?? matched.params.id ?? null) : null;
  const level: LevelId = spec?.level ?? 'district';
  const slice: Record<string, string> = {};
  for (const name of SLICE_PARAMS) {
    const value = params.get(name);
    if (value) slice[name] = value;
  }
  return {
    route: pathname,
    pattern: spec?.path ?? null,
    level,
    object: objectId && spec && spec.level !== 'district' ? { kind: spec.level, id: objectId } : null,
    as_of: params.get('as_of') ?? params.get('date'),
    include_demo: params.get('include_demo') !== 'false',
    slice,
    title: typeof document === 'undefined' ? null : document.title || null,
    extras,
  };
}

export function AgentContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [extras, setExtrasState] = useState<Record<string, PageExtras>>({});
  const current = useRef<PageContext>(pageContextFrom(location.pathname, location.search));

  const merged = useMemo(
    () => Object.values(extras).reduce<PageExtras>((acc, item) => ({ ...acc, ...item }), {}),
    [extras],
  );
  current.current = pageContextFrom(location.pathname, location.search, merged);

  const setExtras = useCallback((owner: string, value: PageExtras | null) => {
    setExtrasState((previous) => {
      const next = { ...previous };
      if (value === null) delete next[owner];
      else next[owner] = value;
      return next;
    });
  }, []);

  const value = useMemo<AgentContextValue>(
    () => ({
      snapshot: () => ({
        ...current.current,
        today: localToday(),
        scope_bar: readScopeBar(),
        screen_text: readScreenText(),
      }),
      setExtras,
    }),
    [setExtras],
  );
  return <AgentCtx.Provider value={value}>{children}</AgentCtx.Provider>;
}

/** Снимок контекста для транспорта панели. Вне провайдера — из адреса окна. */
export function useAgentSnapshot(): () => PageContext {
  const value = useContext(AgentCtx);
  return value?.snapshot ?? (() => pageContextFrom(window.location.pathname, window.location.search));
}

/**
 * Дополнить контекст страницы: `useAgentContext({ selectedContour: 42 })`.
 * Значение сериализуется в JSON и уходит модели как данные, не инструкции.
 */
export function useAgentContext(extras: PageExtras): void {
  const value = useContext(AgentCtx);
  const owner = useRef(`page-${Math.random().toString(36).slice(2)}`);
  const serialized = JSON.stringify(extras);
  useEffect(() => {
    if (!value) return;
    value.setExtras(owner.current, JSON.parse(serialized) as PageExtras);
    const id = owner.current;
    return () => value.setExtras(id, null);
  }, [value, serialized]);
}
